import { Event, SDK, SDKResponse, ServerOptions } from '@commercetools/frontend-sdk';
import { Cart, ShippingMethod, Order } from 'shared/types/cart';
import { PaginatedResult } from 'shared/types/result';
import { Token } from 'shared/types/Token';
import {
  AddCartItemAction,
  CheckoutCartAction,
  CheckoutCartForM2hAction,
  GetAvailableCartShippingMethodsAction,
  requestOrderCancellationAction,
  GetCartAction,
  GetCartShippingMethodsAction,
  GetCheckoutSessionTokenAction,
  GetMiniCartAction,
  GetOrderAction,
  PickUpInfoAction,
  QueryOrdersAction,
  RedeemDiscountCodeAction,
  RemoveCartItemAction,
  RemoveDiscountCodeAction,
  SetCartShippingAction,
  SetCartShippingMethodAction,
  UpdateCartAction,
  UpdateCartItemAction,
  UpdateCartLineItemsAction,
  ValidateCartLineItemsAction,
  RecalculateCartLineItemsAction,
  UpdateImageMetadataIdsAction,
  removeAllLineItems,
  GetPersonalizedCartBanner,
} from '../../types/actions/CartActions';
import { ComposableCommerceEvents } from '../../types/events/ComposableCommerceEvents';
import {
  AddCartItemPayload,
  CheckoutCartPayload,
  GetCartByID,
  RedeemDiscountCodePayload,
  RemoveCartItemPayload,
  RemoveDiscountCodePayload,
  SetCartShippingPayload,
  SetCartShippingMethodPayload,
  UpdateCartItemPayload,
  UpdateCartLineItemPayload,
  UpdateCartPayload,
  PickupInfo,
  ValidateCartLineItemPayload,
  RecalculateCartLineItemPayload,
  PersonalizedCartPayload,
} from '../../types/payloads/CartPayloads';
import { QueryOrdersQuery, GetOrderQuery } from '../../types/queries/CartQueries';

export type CartActions = {
  getCart: GetCartAction;
  addItem: AddCartItemAction;
  removeItem: RemoveCartItemAction;
  updateImageMetadataIds: UpdateImageMetadataIdsAction;
  updateItem: UpdateCartItemAction;
  updateLineItems: UpdateCartLineItemsAction;
  updateCart: UpdateCartAction;
  getShippingMethods: GetCartShippingMethodsAction;
  getAvailableShippingMethods: GetAvailableCartShippingMethodsAction;
  setShipping: SetCartShippingAction;
  setShippingMethod: SetCartShippingMethodAction;
  redeemDiscountCode: RedeemDiscountCodeAction;
  removeDiscountCode: RemoveDiscountCodeAction;
  checkout: CheckoutCartAction;
  checkoutForM2h: CheckoutCartForM2hAction;
  queryOrders: QueryOrdersAction;
  getOrder: GetOrderAction;
  getCheckoutSessionToken: GetCheckoutSessionTokenAction;
  getMiniCarts: GetMiniCartAction;
  setPickInfos: PickUpInfoAction;
  validatecart: ValidateCartLineItemsAction;
  recalculatecart: RecalculateCartLineItemsAction;
  requestOrderCancellation: requestOrderCancellationAction;
  removeAllLineItems: removeAllLineItems;
  getPersonalizedCartBanner: GetPersonalizedCartBanner;
};

const pendingCartRequests = new Map<string, Promise<SDKResponse<unknown>>>();

// Dedupes concurrent, identical cart requests (e.g. rapid double-clicks on the same button)
// by reusing the in-flight promise instead of firing a duplicate network call. Requests
// carrying `serverOptions` (per-request cookies forwarded during SSR) always bypass the cache
// so a response is never shared across different requests/users.
const callCartAction = <ReturnData>(
  sdk: SDK<ComposableCommerceEvents>,
  options: Parameters<SDK<ComposableCommerceEvents>['callAction']>[0],
): Promise<SDKResponse<ReturnData>> => {
  if (options.serverOptions) {
    return sdk.callAction<ReturnData>(options);
  }

  const key = JSON.stringify({ actionName: options.actionName, payload: options.payload, query: options.query });
  const pending = pendingCartRequests.get(key);
  if (pending) {
    return pending as Promise<SDKResponse<ReturnData>>;
  }

  const request = sdk.callAction<ReturnData>(options).finally(() => {
    pendingCartRequests.delete(key);
  });
  pendingCartRequests.set(key, request as Promise<SDKResponse<unknown>>);
  return request;
};

export const getCartActions = (sdk: SDK<ComposableCommerceEvents>): CartActions => {
  return {
    getCart: async (
      payload?: GetCartByID,
      options: {
        cartId?: string;
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await callCartAction<Cart>(sdk, {
        actionName: 'cart/getCart',
        parallel: options.parallel,
        query: payload?.query ?? undefined,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
        payload: options?.cartId ? { cartId: options?.cartId } : undefined,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'cartFetched',
            data: {
              cart: response,
            },
          }),
        );
      }
      return response;
    },
    addItem: async (
      payload: AddCartItemPayload,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await callCartAction<Cart>(sdk, {
        actionName: 'cart/addToCart',
        payload,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'productAddedToCart',
            data: {
              product: payload.variant,
              quantity: payload.variant.count,
            },
          }),
        );
      }
      return response;
    },
    removeItem: async (
      payload: RemoveCartItemPayload,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await callCartAction<Cart>(sdk, {
        actionName: 'cart/removeLineItem',
        payload,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'productRemovedFromCart',
            data: {
              product: payload.lineItem,
              quantity: 1,
            },
          }),
        );
      }
      return response;
    },

    updateImageMetadataIds: async (
      payload: {
        lineItemId: string;
        cartId: string;
        isAddTocart: boolean;
        imageMetadataIds: string[];
      },
      options = {},
    ) => {
      const response = await callCartAction<any>(sdk, {
        actionName: 'cart/updateImageMetadataIds',
        payload,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });
      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'updateImageMetadataIds',
            data: payload,
          }),
        );
      }

      return response;
    },

    removeAllLineItems: async (
      payload: {
        cartIds?: string[];
        wishlistIds?: string[];
      },
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await callCartAction<any>(sdk, {
        actionName: 'cart/removeAllLineItems',
        payload,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'removeAllLineItems',
            data: payload,
          }),
        );
      }

      return response;
    },

    updateItem: async (
      payload: UpdateCartItemPayload,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await callCartAction<Cart>(sdk, {
        actionName: 'cart/updateLineItem',
        payload,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'productUpdatedInCart',
            data: {
              product: {
                id: payload.lineItem.id,
              },
              newQuantity: payload.lineItem.count,
            },
          }),
        );
      }
      return response;
    },
    updateLineItems: async (
      payload: UpdateCartLineItemPayload,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await callCartAction<Cart>(sdk, {
        actionName: 'cart/updateLineItems',
        payload,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'productLineItemUpdatedInCart',
            data: {
              product: payload.cartItems,
            },
          }),
        );
      }
      return response;
    },
    updateCart: async (
      payload: UpdateCartPayload,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await callCartAction<Cart>(sdk, {
        actionName: 'cart/updateCart',
        payload,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'cartUpdated',
            data: payload,
          }),
        );
      }
      return response;
    },
    validatecart: async (
      payload: ValidateCartLineItemPayload,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await callCartAction<Cart>(sdk, {
        actionName: 'cart/validateCart',
        payload,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'validateCartItems',
            data: payload,
          }),
        );
      }
      return response;
    },
    recalculatecart: async (
      payload: RecalculateCartLineItemPayload,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await callCartAction<Cart>(sdk, {
        actionName: 'cart/recalculateCart',
        payload,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'recalculateCartItems',
            data: payload,
          }),
        );
      }
      return response;
    },
    getShippingMethods: async (
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await callCartAction<ShippingMethod[]>(sdk, {
        actionName: 'cart/getShippingMethods',
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'shippingMethodsFetched',
            data: {
              shippingMethods: response.data,
            },
          }),
        );
      }
      return response;
    },
    getAvailableShippingMethods: async (
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await callCartAction<ShippingMethod[]>(sdk, {
        actionName: 'cart/getAvailableShippingMethods',
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'availableShippingMethodsFetched',
            data: {
              shippingMethods: response.data,
            },
          }),
        );
      }
      return response;
    },
    setShipping: async (
      payload: SetCartShippingPayload,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await callCartAction<Cart>(sdk, {
        actionName: 'cart/setShipping',
        payload,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'shippingUpdated',
            data: payload,
          }),
        );
      }
      return response;
    },
    setShippingMethod: async (
      payload: SetCartShippingMethodPayload,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await callCartAction<Cart>(sdk, {
        actionName: 'cart/setShippingMethod',
        payload,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'shippingMethodUpdated',
            data: {
              shippingMethod: response.data.availableShippingMethods?.find(
                (shippingMethod) => shippingMethod.shippingMethodId === payload.shippingMethod.id,
              ),
            },
          }),
        );
      }
      return response;
    },
    redeemDiscountCode: async (
      payload: RedeemDiscountCodePayload,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await callCartAction<Cart | string>(sdk, {
        actionName: 'cart/redeemDiscount',
        payload,
        query: payload.query,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'discountCodeRedeemed',
            data: {
              discountCode: typeof response.data !== 'string' ? payload.code : response.data,
              cart: typeof response.data !== 'string' ? response.data : undefined,
            },
          }),
        );
      }
      return response;
    },
    removeDiscountCode: async (
      payload: RemoveDiscountCodePayload,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await callCartAction<Cart>(sdk, {
        actionName: 'cart/removeDiscount',
        payload,
        query: payload.query,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'discountCodeRemoved',
            data: {
              discountCode: payload.discountCodeId,
              cart: response.data,
            },
          }),
        );
      }
      return response;
    },
    checkout: async (
      payload: CheckoutCartPayload,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await callCartAction<Cart>(sdk, {
        actionName: 'cart/checkout',
        payload,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'cartCheckedOut',
            data: {},
          }),
        );
      }
      return response;
    },
    checkoutForM2h: async (
      query: { cartId: string },
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await callCartAction<Cart>(sdk, {
        actionName: 'cart/checkoutForM2h',
        query,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'cartCheckedOut',
            data: {},
          }),
        );
      }
      return response;
    },
    queryOrders: async (
      query?: QueryOrdersQuery,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await callCartAction<PaginatedResult<Order>>(sdk, {
        actionName: 'cart/queryOrders',
        query,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });
      return response;
    },
    getOrder: async (
      query: GetOrderQuery,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await callCartAction<Order>(sdk, {
        actionName: 'cart/getOrder',
        query,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });
      return response;
    },
    getCheckoutSessionToken: async (
      query?: {
        cartId?: string;
        fulfillerCode?: string;
      },
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await callCartAction<Token>(sdk, {
        actionName: 'cart/getCheckoutSessionToken',
        query,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });
      return response;
    },
    getMiniCarts: async (
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await callCartAction<Cart[]>(sdk, {
        actionName: 'cart/getMiniCarts',
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'cartFetched',
            data: {
              cart: response,
            },
          }),
        );
      }
      return response;
    },
    setPickInfos: async (
      query: { cartId: string },
      payload: PickupInfo,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await callCartAction<any>(sdk, {
        actionName: 'cart/setPickupInfo',
        query,
        payload,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError && response.data) {
        sdk.trigger(
          new Event({
            eventName: 'searchTermsFetched',
            data: {
              searchTerms: response.data,
            },
          }),
        );
      }
      return response;
    },
    requestOrderCancellation: async (
      query: GetOrderQuery,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await callCartAction<Order>(sdk, {
        actionName: 'cart/requestOrderCancellation',
        query,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });
      return response;
    },
    getPersonalizedCartBanner: async (
      payload: PersonalizedCartPayload,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await callCartAction<Cart[]>(sdk, {
        actionName: 'cart/getPersonalizedCartBanner',
        query: payload?.query ?? undefined,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'cartFetched',
            data: {
              cart: response,
            },
          }),
        );
      }
      return response;
    },
  };
};
